/**
 * Hanging-protocol engine (MVP, Sprint 3).
 *
 * A hanging protocol is a set of RULES that say: "when a study
 * matches CONDITION, open series S in layout L, with W/L preset P".
 * The engine evaluates the first matching rule in a protocol list
 * and returns the display-set directive the viewer should honour.
 *
 * Example protocol JSON:
 *
 * {
 *   "id": "chest-ct-default",
 *   "name": "Chest CT — default",
 *   "rules": [
 *     { "modality": "CT", "descriptionContains": "CHEST", "preset": "Lung", "layout": "1x1" }
 *   ]
 * }
 *
 * Protocols live as static assets at /viewer-api/hanging-protocols/*
 * (returned by viewer-api). Sprint 4 gains a rule editor; Sprint 5
 * gains per-user preference persistence.
 */

import type { WLPreset } from "./presets";

export interface HangingRule {
  modality?: string;
  descriptionContains?: string;
  preset?: string;
  layout?: "1x1" | "1x2" | "2x1" | "2x2";
}

export interface HangingProtocol {
  id: string;
  name: string;
  rules: HangingRule[];
}

export interface Directive {
  layout: "1x1" | "1x2" | "2x1" | "2x2";
  preset?: string;
}

export function apply(
  protocols: HangingProtocol[],
  study: { modality?: string; description?: string },
): Directive | null {
  for (const proto of protocols) {
    for (const rule of proto.rules) {
      if (matches(rule, study)) {
        return {
          layout: rule.layout ?? "1x1",
          preset: rule.preset,
        };
      }
    }
  }
  return null;
}

function matches(rule: HangingRule, study: { modality?: string; description?: string }): boolean {
  if (rule.modality) {
    if (!study.modality) return false;
    if (study.modality.toUpperCase() !== rule.modality.toUpperCase()) return false;
  }
  if (rule.descriptionContains) {
    const d = (study.description ?? "").toLowerCase();
    if (!d.includes(rule.descriptionContains.toLowerCase())) return false;
  }
  return true;
}

/** Default built-in protocols — ship with the viewer so it does
 *  something sensible before any hospital writes its own. */
export const DEFAULT_PROTOCOLS: HangingProtocol[] = [
  {
    id: "ct-default",
    name: "CT — abdomen first",
    rules: [
      { modality: "CT", descriptionContains: "chest",   preset: "Lung",    layout: "1x1" },
      { modality: "CT", descriptionContains: "head",    preset: "Brain",   layout: "1x1" },
      { modality: "CT", descriptionContains: "angio",   preset: "Angio",   layout: "1x1" },
      { modality: "CT",                                    preset: "Abdomen", layout: "1x1" },
    ],
  },
  {
    id: "mr-default",
    name: "MR — default",
    rules: [
      { modality: "MR", preset: "Default", layout: "1x1" },
    ],
  },
  {
    id: "xray-default",
    name: "X-ray — default",
    rules: [
      { modality: "CR", preset: "Soft tissue", layout: "1x1" },
      { modality: "DX", preset: "Soft tissue", layout: "1x1" },
    ],
  },
];

export function pickPreset(
  protocols: HangingProtocol[],
  study: { modality?: string; description?: string },
): string | undefined {
  return apply(protocols, study)?.preset;
}
